import { FC, useEffect, useRef, useState } from 'react';
import { ElementThemeUnion } from 'constants/routes';

import { genericMemo } from 'utils';

import Dropdown from './Dropdown';
import MultiSelectBase from './MultiSelectBase';

interface MultiSelectProps {
  value: string[];
  onSelect: (selected: string[]) => void;
  options: { label: string; value: number }[];
  placeholder: string;

  endIcon?: React.ReactNode;
  selectedCount?: number;
  searchPlaceholder?: string;
  theme?: ElementThemeUnion;
}

const MultiSelect: FC<MultiSelectProps> = ({
  value,
  onSelect,
  options,
  placeholder,
  endIcon,
  selectedCount,
  searchPlaceholder = '',
  theme = 'dark',
}): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showOptions, setShowOptions] = useState<boolean>(false);

  const multiSelectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleToggleOption = (selectedOption: string) => {
    const newSelectedOptions = value.includes(selectedOption)
      ? value.filter((option) => option !== selectedOption)
      : [...value, selectedOption];

    setSearchQuery('');
    onSelect(newSelectedOptions);
  };

  const closeDropdown = () => {
    setShowOptions(false);
  };

  useEffect(() => {
    if (showOptions && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showOptions, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        multiSelectRef.current &&
        !multiSelectRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  return (
    <div className="relative w-full" ref={multiSelectRef}>
      <MultiSelectBase
        value={value}
        onToggle={() => setShowOptions((prev) => !prev)}
        showOptions={showOptions}
        placeholder={placeholder}
        endIcon={endIcon}
        selectedCount={selectedCount}
        theme={theme}
      />
      {showOptions && (
        <Dropdown
          options={options}
          value={value}
          onSelect={handleToggleOption}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchPlaceholder={searchPlaceholder}
          theme={theme}
        />
      )}
    </div>
  );
};

export default genericMemo(MultiSelect);
